import { useCallback, useEffect, useState } from 'react';
import { FiImage } from 'react-icons/fi';
import { toast } from 'react-toastify';
import PageHeader from '../components/PageHeader';
import Button from '../components/Button';
import Field from '../components/Field';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  brandingService,
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_SECONDARY_COLOR,
  OrganizationBranding,
} from '../services/brandingService';
import { inputClass } from '../utils/formStyles';

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

const emptyBranding = (): OrganizationBranding => ({
  primaryColor: DEFAULT_PRIMARY_COLOR,
  secondaryColor: DEFAULT_SECONDARY_COLOR,
  logoUrl: null,
});

function messageFrom(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { error?: string; message?: string } | string } })?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object') return data.error || data.message || fallback;
  return fallback;
}

const LogoPreview = ({ src, alt }: { src: string | null; alt: string }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);

  if (!src || failed) {
    return (
      <div className="h-28 w-28 rounded-control border border-dashed border-line bg-surface-muted flex flex-col items-center justify-center text-ink-subtle">
        <FiImage aria-hidden="true" className="text-2xl" />
        <span className="mt-1 text-meta">No logo</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
      className="h-28 w-28 rounded-control border border-line bg-surface object-contain"
    />
  );
};

const CompanyProfile = () => {
  const [branding, setBranding] = useState<OrganizationBranding>(emptyBranding);
  const [draft, setDraft] = useState<OrganizationBranding>(emptyBranding);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fileError, setFileError] = useState('');

  const load = useCallback(async () => {
    try {
      setBranding(await brandingService.get());
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setBranding(emptyBranding());
        return;
      }
      toast.error(messageFrom(error, 'Could not load company branding.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const startEdit = () => {
    setDraft(branding);
    setFile(null);
    setFileError('');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setFile(null);
    setFileError('');
  };

  const onFile = (selected: File | undefined) => {
    if (!selected) {
      setFile(null);
      setFileError('');
      return;
    }
    if (!ACCEPTED_TYPES.includes(selected.type)) {
      setFile(null);
      setFileError('Use a PNG, JPEG, WEBP, or GIF image.');
      return;
    }
    if (selected.size > MAX_LOGO_BYTES) {
      setFile(null);
      setFileError('Logo must be 2 MB or smaller.');
      return;
    }
    setFileError('');
    setFile(selected);
  };

  const save = async () => {
    if (!/^#[0-9A-F]{6}$/.test(draft.primaryColor) || !/^#[0-9A-F]{6}$/.test(draft.secondaryColor)) {
      toast.error('Colors must be hex values like #3B82F6.');
      return;
    }
    setSaving(true);
    try {
      await brandingService.update({
        primaryColor: draft.primaryColor,
        secondaryColor: draft.secondaryColor,
        logo: file,
      });
      const refreshed = await brandingService.get();
      setBranding(refreshed);
      setEditing(false);
      setFile(null);
      toast.success('Company profile saved.');
    } catch (error) {
      toast.error(messageFrom(error, 'Could not save company branding.'));
    } finally {
      setSaving(false);
    }
  };

  const shownLogo = editing ? previewUrl ?? branding.logoUrl : branding.logoUrl;

  return (
    <div className="p-8 lg:p-10">
      <PageHeader
        title="Company profile"
        subtitle="Organization logo and brand colors, stored in Product Hub."
        actions={
          !loading && !editing ? (
            <Button variant="primary" onClick={startEdit}>Edit</Button>
          ) : null
        }
      />
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-surface border border-line rounded-azure-sm p-6 max-w-xl">
          <div className="flex items-center gap-5">
            <LogoPreview src={shownLogo} alt="Company logo" />
            <div>
              <p className="font-medium text-ink">Logo</p>
              <p className="text-meta text-ink-subtle">
                {editing && file ? file.name : 'Shown wherever this organization is branded.'}
              </p>
            </div>
          </div>

          {editing ? (
            <div className="mt-6 space-y-4">
              <Field htmlFor="company-logo" label="Upload new logo" error={fileError} help="PNG, JPEG, WEBP, or GIF. 2 MB maximum.">
                <input
                  id="company-logo"
                  type="file"
                  accept={ACCEPTED_TYPES.join(',')}
                  onChange={(event) => onFile(event.target.files?.[0])}
                  aria-invalid={fileError ? true : undefined}
                  aria-describedby={fileError ? 'company-logo-error' : 'company-logo-help'}
                  className="block w-full text-body text-ink file:mr-3 file:h-9 file:px-3 file:rounded-control file:border file:border-line file:bg-surface-muted file:text-body file:font-medium"
                />
              </Field>
              <Field htmlFor="primary-color" label="Primary color">
                <div className="flex items-center gap-3">
                  <input
                    id="primary-color"
                    type="color"
                    value={draft.primaryColor.toLowerCase()}
                    onChange={(event) => setDraft((current) => ({ ...current, primaryColor: event.target.value.toUpperCase() }))}
                    className="h-10 w-14 cursor-pointer rounded-control border border-line bg-surface p-1"
                  />
                  <input
                    aria-label="Primary color hex"
                    value={draft.primaryColor}
                    onChange={(event) => setDraft((current) => ({ ...current, primaryColor: event.target.value.toUpperCase() }))}
                    className={inputClass}
                  />
                </div>
              </Field>
              <Field htmlFor="secondary-color" label="Secondary color">
                <div className="flex items-center gap-3">
                  <input
                    id="secondary-color"
                    type="color"
                    value={draft.secondaryColor.toLowerCase()}
                    onChange={(event) => setDraft((current) => ({ ...current, secondaryColor: event.target.value.toUpperCase() }))}
                    className="h-10 w-14 cursor-pointer rounded-control border border-line bg-surface p-1"
                  />
                  <input
                    aria-label="Secondary color hex"
                    value={draft.secondaryColor}
                    onChange={(event) => setDraft((current) => ({ ...current, secondaryColor: event.target.value.toUpperCase() }))}
                    className={inputClass}
                  />
                </div>
              </Field>
              <div className="flex items-center gap-2 pt-2">
                <Button variant="primary" loading={saving} onClick={save}>Save</Button>
                <Button variant="secondary" disabled={saving} onClick={cancelEdit}>Cancel</Button>
              </div>
            </div>
          ) : (
            <dl className="mt-6 grid grid-cols-2 gap-4">
              <div>
                <dt className="text-meta text-ink-subtle">Primary color</dt>
                <dd className="mt-1 flex items-center gap-2 text-body text-ink">
                  <span className="h-4 w-4 rounded-sm border border-line" style={{ backgroundColor: branding.primaryColor }} />
                  {branding.primaryColor}
                </dd>
              </div>
              <div>
                <dt className="text-meta text-ink-subtle">Secondary color</dt>
                <dd className="mt-1 flex items-center gap-2 text-body text-ink">
                  <span className="h-4 w-4 rounded-sm border border-line" style={{ backgroundColor: branding.secondaryColor }} />
                  {branding.secondaryColor}
                </dd>
              </div>
            </dl>
          )}
        </div>
      )}
    </div>
  );
};

export default CompanyProfile;
